import { useCallback, useEffect, useRef, useState } from "react";
import { formatBytes, type Identity, type JmapClient } from "../lib/jmap";
import { htmlToText, textToHtml } from "../lib/html-to-text";
import { prepareInlineImages, type InlineImage } from "../lib/inline-images";
import { Editor } from "./Editor";
import { Icon } from "./Icon";

/** A file that has been uploaded and is waiting to be referenced by the send. */
interface PendingAttachment {
  /** Stable across re-renders; File objects are not comparable by identity here. */
  key: string;
  name: string;
  size: number;
  type: string;
  /** null while the bytes are still going up. */
  blobId: string | null;
  error?: string;
}

/** Parses "Name <a@b.c>, d@e.f" into JMAP address objects. */
function parseRecipients(value: string) {
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      const m = entry.match(/^(.*?)\s*<([^>]+)>$/);
      return m
        ? { name: m[1].replace(/^"|"$/g, "").trim() || null, email: m[2].trim() }
        : { name: null, email: entry };
    });
}

const ADDRESS = /^[^\s@,;]+@[^\s@,;.]+\.[^\s@,;]+$/;

export interface Draft {
  to: string;
  cc?: string;
  subject: string;
  /** Plain text — a quoted reply, or a resumed draft's text part. */
  body: string;
  /** Set when resuming a draft that was composed with formatting. */
  html?: string | null;
  inReplyTo?: string[] | null;
  references?: string[] | null;
  /** Set when resuming from the Drafts folder — the saved copy to supersede. */
  draftId?: string | null;
  /** Attachments already on a resumed draft. */
  attachments?: { blobId: string; type: string; name: string; size: number }[];
}

/** How long the user has to stop typing before the draft is written. */
const AUTOSAVE_IDLE_MS = 2500;

export function Compose({
  client, from, initial, onClose, onDiscard, onSent, undoSendSeconds, onHeld, alwaysShowCc, signatureOnReplies,
}: {
  client: JmapClient;
  from: string;
  initial: Draft;
  /** Closes the composer, keeping whatever has been autosaved. */
  onClose: () => void;
  /** Closes the composer after the saved copy has been thrown away. */
  onDiscard: () => void;
  onSent: () => void;
  /**
   * Seconds to hold the message before it actually goes, and the callback that
   * offers the reader a way to stop it. Zero means send immediately.
   */
  undoSendSeconds: number;
  onHeld: (send: () => Promise<void>, cancel: () => void) => void;
  /** Outlook's "Always show Bcc/Cc". */
  alwaysShowCc: boolean;
  /** Outlook's "Include my signature on replies". */
  signatureOnReplies: boolean;
}) {
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc ?? "");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(!!initial.cc || alwaysShowCc);
  const [subject, setSubject] = useState(initial.subject);
  // The composer is rich text, so HTML is the working copy. `initial.body` is
  // plain (a quoted reply, a resumed draft's text part), so it is converted in
  // rather than the editor being asked to cope with two formats.
  // Genuinely empty stays empty: textToHtml("") would produce "<div><br></div>",
  // which is not `:empty`, so the placeholder would never show.
  const [html, setHtml] = useState(
    () => initial.html ?? (initial.body ? textToHtml(initial.body) : ""),
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>(() =>
    (initial.attachments ?? []).map((a, i) => ({
      key: `r${i}`, name: a.name, size: a.size, type: a.type, blobId: a.blobId,
    })),
  );
  const [draftId, setDraftId] = useState<string | null>(initial.draftId ?? null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const filePicker = useRef<HTMLInputElement>(null);
  const nextKey = useRef(0);
  /**
   * Images pasted or dropped into the body.
   *
   * A ref rather than state: these do not affect what is rendered — the editor
   * already shows its own preview — and putting them in state would re-render
   * the editor mid-typing and move the caret.
   */
  const inlineImages = useRef<InlineImage[]>([]);
  const objectUrls = useRef<string[]>([]);

  // Object URLs live as long as the composer does; without this every pasted
  // screenshot stays in memory for the life of the tab.
  useEffect(() => () => objectUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  /** Uploads a pasted or dropped image and hands the editor a local preview. */
  const insertImage = useCallback(async (file: File) => {
    try {
      const blob = await client.upload(file);
      const id = `img-${nextKey.current++}`;
      inlineImages.current.push({
        id,
        blobId: blob.blobId,
        type: blob.type || file.type || "image/png",
        name: file.name || "image.png",
      });
      const url = URL.createObjectURL(file);
      objectUrls.current.push(url);
      return { id, url };
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add that image.");
      return null;
    }
  }, [client]);

  const uploading = attachments.some((a) => !a.blobId && !a.error);

  // Resolve the sending identity up front so a missing one surfaces while the
  // message is still being written, not at the moment the user hits Send.
  useEffect(() => {
    let live = true;
    client
      .identityFor(from)
      .then((i) => live && setIdentity(i))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : "No sending identity"));
    return () => {
      live = false;
    };
  }, [client, from]);

  /**
   * Uploads each file as it is picked, rather than at send time.
   *
   * Uploading up front means a slow 20 MB attachment is waiting during the time
   * the message is being written, not after Send is pressed — and a file the
   * server refuses is reported while there is still something to do about it.
   */
  function attach(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const key = `f${nextKey.current++}`;
      setAttachments((prev) => [
        ...prev,
        { key, name: file.name, size: file.size, type: file.type || "application/octet-stream", blobId: null },
      ]);
      client
        .upload(file)
        .then((blob) =>
          setAttachments((prev) =>
            prev.map((a) => (a.key === key ? { ...a, blobId: blob.blobId, type: blob.type || a.type } : a)),
          ),
        )
        .catch((err: unknown) =>
          setAttachments((prev) =>
            prev.map((a) =>
              a.key === key
                ? { ...a, error: err instanceof Error ? err.message : "Upload failed" }
                : a,
            ),
          ),
        );
    }
    // Reset the input so picking the same file twice in a row still fires.
    if (filePicker.current) filePicker.current.value = "";
  }

  /** The message as it stands, in the shape both save and send take. */
  const message = useCallback(() => {
    // Local previews become cid: references, and the images they point at
    // become inline parts alongside the ordinary attachments.
    const prepared = prepareInlineImages(html, inlineImages.current);
    return {
      to: parseRecipients(to),
      cc: parseRecipients(cc),
      bcc: parseRecipients(bcc),
      subject,
      // Both parts, always. The text alternative is derived from what was
      // actually written rather than kept as a separate draft the user never
      // sees, so the two can never drift apart.
      html: prepared.html,
      text: htmlToText(prepared.html),
      // A reply is one that carries threading headers.
      includeSignature: initial.inReplyTo?.length ? signatureOnReplies : true,
      inReplyTo: initial.inReplyTo,
      references: initial.references,
      attachments: [
        ...attachments
          .filter((a) => a.blobId)
          .map((a) => ({ blobId: a.blobId!, type: a.type, name: a.name })),
        ...prepared.attachments,
      ],
    };
  }, [to, cc, bcc, subject, html, attachments, initial.inReplyTo, initial.references, signatureOnReplies]);

  /**
   * Autosave.
   *
   * Closing the composer — or closing the tab, or a reload — used to lose
   * whatever had been written. The draft is now written to the Drafts folder
   * after a pause in typing, so the worst case is losing the last few seconds
   * rather than the whole message. Nothing is saved until there is something
   * worth saving, so opening and closing the composer does not litter Drafts
   * with empty messages.
   */
  // Empty contenteditable content is "<br>" or "<div><br></div>", not "" — so
  // this has to ask whether there is any TEXT, or every opened composer would
  // autosave a blank draft.
  const hasContent = !!(
    to.trim() || subject.trim() || htmlToText(html).trim() || attachments.length
  );

  useEffect(() => {
    if (!identity || !hasContent || sending || uploading) return;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      try {
        const boxes = await client.mailboxes();
        const draftsId = boxes.find((m) => m.role === "drafts")?.id;
        if (!draftsId || cancelled) return;
        const id = await client.saveDraft(message(), draftsId, identity, draftId);
        if (cancelled) return;
        setDraftId(id);
        setSavedAt(new Date());
        setSaveFailed(false);
      } catch {
        // Not an error banner — that would fight the person typing. But it must
        // be visible: a silent autosave is indistinguishable from a working one
        // right up until the message is lost. A `cc: null` that Stalwart
        // rejected sat here looking exactly like success.
        if (!cancelled) setSaveFailed(true);
      }
    }, AUTOSAVE_IDLE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // draftId is deliberately NOT a dependency: it changes on every save, and
    // including it would make each save immediately schedule the next one.
  }, [client, identity, message, hasContent, sending, uploading]);

  async function send() {
    const recipients = parseRecipients(to);
    const ccList = parseRecipients(cc);
    const bccList = parseRecipients(bcc);
    if (recipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    const bad = [...recipients, ...ccList, ...bccList].find((r) => !ADDRESS.test(r.email));
    if (bad) {
      setError(`"${bad.email}" is not a valid email address.`);
      return;
    }
    if (!identity) {
      setError("Still preparing this mailbox. Try again in a moment.");
      return;
    }
    if (uploading) {
      setError("Still uploading an attachment. One moment.");
      return;
    }
    const failed = attachments.find((a) => a.error);
    if (failed) {
      setError(`"${failed.name}" did not attach. Remove it or try again.`);
      return;
    }

    setSending(true);
    setError(null);

    // Snapshot everything now: the composer closes immediately, so the deferred
    // send must not read state that is about to be unmounted.
    const payload = { ...message(), to: recipients, cc: ccList, bcc: bccList };
    const savedDraftId = draftId;

    const deliver = async () => {
      const boxes = await client.mailboxes();
      const draftsId = boxes.find((m) => m.role === "drafts")?.id;
      const sentId = boxes.find((m) => m.role === "sent")?.id;
      if (!draftsId || !sentId) throw new Error("Drafts or Sent folder is missing");
      await client.send(payload, { draftsId, sentId }, identity, savedDraftId);
    };

    if (undoSendSeconds > 0) {
      // Outlook's undo send. The composer closes as if the message had gone,
      // because that is what makes it feel sent; the message itself waits. The
      // draft is deliberately left in place until delivery succeeds, so undoing
      // — or closing the tab during the wait — leaves it recoverable.
      onHeld(deliver, () => {
        setSending(false);
      });
      onSent();
      return;
    }

    try {
      await deliver();
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send. Please try again.");
      setSending(false);
    }
  }

  return (
    // `.composer` is display:none until `.on` is added — the design toggles
    // visibility with the class rather than mounting/unmounting.
    <div className="composer on" data-screen-label="Composer">
      <div className="cmp-h">
        <b>{draftId ? "Draft" : "New message"}</b>
        {saveFailed ? (
          <span style={{ font: "500 11px/1 Inter", color: "var(--warn)" }} role="status">
            Not saved — keep this window open
          </span>
        ) : savedAt ? (
          <span style={{ font: "400 11px/1 Inter", color: "var(--tx3)" }} role="status">
            Saved {savedAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </span>
        ) : null}
        <button className="ibtn x" onClick={onClose} title="Close — the draft is kept">
          <Icon name="x" />
        </button>
      </div>

      <div className="cmp-f">
        <label>To</label>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="name@domain.com"
          autoFocus
        />
        {!showCc && (
          <button type="button" className="cc" onClick={() => setShowCc(true)}>
            Cc/Bcc
          </button>
        )}
      </div>

      {showCc && (
        <>
          <div className="cmp-f">
            <label>Cc</label>
            <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="name@domain.com" />
          </div>
          <div className="cmp-f">
            <label>Bcc</label>
            <input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="name@domain.com" />
          </div>
        </>
      )}

      <div className="cmp-f">
        <label>Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>

      <Editor
        value={html}
        onChange={setHtml}
        onSend={() => void send()}
        onInsertImage={insertImage}
        placeholder="Write your message…"
      />

      {attachments.length > 0 && (
        <div className="atts" style={{ padding: "0 16px 12px" }}>
          {attachments.map((a) => (
            <div key={a.key} className="att" style={a.error ? { borderColor: "var(--bad)" } : undefined}>
              <span className="ic"><Icon name="paperclip" /></span>
              <span>
                <b>{a.name}</b>
                <span>
                  {a.error ? a.error : a.blobId ? formatBytes(a.size) : `Uploading… ${formatBytes(a.size)}`}
                </span>
              </span>
              <button
                className="ibtn"
                title={`Remove ${a.name}`}
                onClick={() => setAttachments((prev) => prev.filter((x) => x.key !== a.key))}
              >
                <Icon name="x" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: "var(--bad)", font: "400 12px/1.4 Inter", padding: "0 16px 8px" }}>
          {error}
        </div>
      )}

      <div className="cmp-t">
        <button className="send" onClick={() => void send()} disabled={sending || uploading}>
          {sending ? "Sending…" : "Send"}
          <Icon name="send-horizontal" />
        </button>
        <button className="ibtn" title="Attach a file" onClick={() => filePicker.current?.click()}>
          <Icon name="paperclip" />
        </button>
        <input
          ref={filePicker}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => attach(e.target.files)}
          aria-label="Attach files"
        />
        <button
          className="ibtn del"
          title="Discard this message"
          onClick={() => {
            // Explicit discard removes the saved copy too — otherwise "discard"
            // would leave the message sitting in Drafts, which is the opposite
            // of what the button says.
            if (draftId) void client.destroy([draftId]).catch(() => {});
            onDiscard();
          }}
        >
          <Icon name="trash-2" />
        </button>
      </div>
    </div>
  );
}
